import { useTheme } from '@mui/material';
import { LineChart } from '@mui/x-charts-pro';
import { memo, useCallback, useMemo } from 'react';
import { CardPaper, formatCompact } from '../lib';
import { CardHeader } from './CardHeader';
import { ChartDataTable } from './ChartDataTable';

interface LineChartCardProps {
  title: string;
  labels: string[];
  values: number[];
  tooltipFormatter: (value: number | null, ctx: { dataIndex: number }) => string;
  tooltip: string;
  onPointClick?: (index: number) => void;
}

export const LineChartCard = memo(function LineChartCard({
  title,
  labels,
  values,
  tooltipFormatter,
  tooltip,
  onPointClick,
}: LineChartCardProps) {
  const theme = useTheme();

  // Numeric indices as the underlying x-axis values so duplicate labels
  // (e.g. two events on the same day) get distinct positions and unique
  // React keys; valueFormatter maps each index back to the human-readable
  // label for display.
  const indices = useMemo(() => labels.map((_, i) => i), [labels]);

  const formatLabelByIndex = useCallback((idx: number) => labels[idx] ?? '', [labels]);

  const xAxis = useMemo(
    () => [{ scaleType: 'point' as const, data: indices, valueFormatter: formatLabelByIndex }],
    [indices, formatLabelByIndex],
  );

  const yAxis = useMemo(
    () => [{ valueFormatter: formatCompact, max: Math.ceil(Math.max(...values) * 1.01) }],
    [values],
  );

  const series = useMemo(
    () => [
      {
        data: values,
        color: theme.palette.primary.main,
        showMark: values.length < 50,
        valueFormatter: tooltipFormatter,
      },
    ],
    [values, theme.palette.primary.main, tooltipFormatter],
  );

  return (
    <CardPaper>
      <CardHeader title={title} tooltip={tooltip} />
      <ChartDataTable
        formatValue={(value, index) => tooltipFormatter(value, { dataIndex: index })}
        labels={labels}
        title={title}
        values={values}
      />
      <LineChart
        xAxis={xAxis}
        yAxis={yAxis}
        series={series}
        height={300}
        margin={{ left: 64, right: 16, top: 16, bottom: 32 }}
        slotProps={{ legend: { hidden: true } }}
        onAxisClick={
          onPointClick &&
          ((_, params) => {
            if (params?.dataIndex != null) onPointClick(params.dataIndex);
          })
        }
        sx={onPointClick && { '&:has(.MuiChartsAxisHighlight-root)': { cursor: 'pointer' } }}
      />
    </CardPaper>
  );
});
